import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts'
import { MapContainer, TileLayer, CircleMarker, Tooltip as LTooltip } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { useAnalyticsSummary, useAnalyticsCampaigns, useAnalyticsGeo, useAnalyticsUsers } from '../hooks/analytics'
import type { GeoStat, DailyStat, ProductDailyStat } from '../api/client'
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  LinkButton,
  PageHeader,
  SegmentedControl,
  Skeleton,
  Spinner,
} from '../components/ui'

const RANGES = [
  { value: '7', label: '7d' },
  { value: '30', label: '30d' },
  { value: '90', label: '90d' },
] as const

type RangeValue = (typeof RANGES)[number]['value']

/* ── Helpers ──────────────────────────────────────────────────────────────── */

function fmt(n: number, decimals = 0) {
  return n.toLocaleString(undefined, { maximumFractionDigits: decimals })
}

function TrendBadge({ value }: { value: number }) {
  if (value === 0) return <span className="text-xs text-gray-400 ml-1.5">-</span>
  const positive = value > 0
  return (
    <span
      className={`inline-flex items-center text-xs font-medium ml-1.5 ${
        positive ? 'text-emerald-600' : 'text-red-500'
      }`}
    >
      {positive ? '↑' : '↓'} {fmt(Math.abs(value), 1)}%
    </span>
  )
}

/* ── Stat card with optional trend ────────────────────────────────────────── */

function StatCard({
  label,
  value,
  sub,
  trend,
  accent,
}: {
  label: string
  value: string | number
  sub?: string
  trend?: number
  accent?: string
}) {
  return (
    <Card className="relative overflow-hidden">
      {accent && (
        <div className={`absolute top-0 left-0 right-0 h-1 ${accent}`} />
      )}
      <p className="text-sm text-gray-500">{label}</p>
      <div className="flex items-baseline mt-1">
        <p className="text-2xl font-semibold text-gray-900">{value}</p>
        {trend !== undefined && <TrendBadge value={trend} />}
      </div>
      {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
    </Card>
  )
}

/* ── Section header ───────────────────────────────────────────────────────── */

function SectionHeader({
  title,
  description,
}: {
  title: string
  description?: string
}) {
  return <CardHeader title={title} description={description} className="mb-4" />
}

/* ── Funnel visualization ─────────────────────────────────────────────────── */

function FunnelStep({
  label,
  value,
  rate,
  color,
  width,
}: {
  label: string
  value: number
  rate?: string
  color: string
  width: string
}) {
  return (
    <div className="flex flex-col items-center flex-1 min-w-0">
      <div
        className={`${color} rounded-lg flex items-center justify-center py-3 text-white font-semibold text-lg transition-all`}
        style={{ width }}
      >
        {fmt(value)}
      </div>
      <p className="text-xs font-medium text-gray-700 mt-2 text-center">{label}</p>
      {rate && <p className="text-xs text-gray-400">{rate}</p>}
    </div>
  )
}

function FunnelArrow() {
  return (
    <div className="flex items-center justify-center px-1 text-gray-300 text-xl self-start mt-3">
      →
    </div>
  )
}

function ConversionFunnel({
  visitors,
  checkouts,
  signups,
  subscriptions,
  visitToSignup,
  signupToPaid,
}: {
  visitors: number
  checkouts: number
  signups: number
  subscriptions: number
  visitToSignup: number
  signupToPaid: number
}) {
  const { t } = useTranslation()
  return (
    <Card className="mb-6">
      <h3 className="text-sm font-medium text-gray-700 mb-5">{t('analytics.funnelTitle')}</h3>
      <div className="flex items-start justify-center gap-0">
        <FunnelStep label={t('analytics.funnelVisitors')} value={visitors} color="bg-sky-500" width="100%" />
        <FunnelArrow />
        <FunnelStep
          label={t('analytics.funnelSignups')}
          value={signups}
          rate={t('analytics.funnelOfVisitors', { pct: fmt(visitToSignup, 1) })}
          color="bg-amber-500"
          width="85%"
        />
        <FunnelArrow />
        <FunnelStep
          label={t('analytics.funnelCheckouts')}
          value={checkouts}
          rate={signups > 0 ? t('analytics.funnelOfSignups', { pct: fmt((checkouts / signups) * 100, 1) }) : undefined}
          color="bg-emerald-500"
          width="65%"
        />
        <FunnelArrow />
        <FunnelStep
          label={t('analytics.funnelPaid')}
          value={subscriptions}
          rate={t('analytics.funnelOfSignups', { pct: fmt(signupToPaid, 1) })}
          color="bg-indigo-500"
          width="45%"
        />
      </div>
    </Card>
  )
}

/* ── Bot traffic panel ────────────────────────────────────────────────────── */

function BotTrafficPanel({
  botPageViews,
  humanPageViews,
  botVisitors,
  humanVisitors,
  botPercentage,
}: {
  botPageViews: number
  humanPageViews: number
  botVisitors: number
  humanVisitors: number
  botPercentage: number
}) {
  const { t } = useTranslation()
  const data = [
    { name: t('analytics.labelPageViews'), Human: humanPageViews, Bot: botPageViews },
    { name: t('analytics.labelVisitors'), Human: humanVisitors, Bot: botVisitors },
  ]

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-700">{t('analytics.botVsHuman')}</h3>
        <Badge tone={botPercentage > 50 ? 'danger' : botPercentage > 20 ? 'warning' : 'success'}>
          {t('analytics.botPercentage', { pct: fmt(botPercentage, 1) })}
        </Badge>
      </div>
      <p className="text-xs text-gray-400 mb-4">
        {t('analytics.botDetectionNote')}
      </p>
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" className="stroke-gray-200" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: '#6b7280', className: 'fill-gray-500' }} />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 12, fill: '#6b7280', className: 'fill-gray-500' }}
            width={80}
          />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="Human" name={t('analytics.labelHuman')} fill="#6366f1" radius={[0, 4, 4, 0]} />
          <Bar dataKey="Bot" name={t('analytics.labelBot')} fill="#e5e7eb" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  )
}

/* ── Daily breakdown table ───────────────────────────────────────────────── */

function DayTrend({ current, previous }: { current: number; previous: number | undefined }) {
  if (previous === undefined) return <span className="text-xs text-gray-300">-</span>
  if (previous === 0 && current === 0) return <span className="text-xs text-gray-300">-</span>
  const pct = previous === 0 ? 100 : ((current - previous) / previous) * 100
  if (Math.abs(pct) < 0.5) return <span className="text-xs text-gray-400">0%</span>
  const positive = pct > 0
  return (
    <span className={`text-xs font-medium ${positive ? 'text-emerald-600' : 'text-red-500'}`}>
      {positive ? '↑' : '↓'} {fmt(Math.abs(pct), 1)}%
    </span>
  )
}

function DailyBreakdownTable({
  acquisitionStats,
  productStats,
}: {
  acquisitionStats: DailyStat[]
  productStats: ProductDailyStat[]
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const productByDate = useMemo(() => {
    const m = new Map<string, ProductDailyStat>()
    for (const p of productStats) m.set(p.date, p)
    return m
  }, [productStats])

  // Reverse so most recent day is first
  const rows = useMemo(() => [...acquisitionStats].reverse(), [acquisitionStats])
  const visibleRows = expanded ? rows : rows.slice(0, 7)

  if (rows.length === 0) return null

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-700">{t('analytics.dailyBreakdown')}</h3>
        <span className="text-xs text-gray-400">{t('analytics.vsPreviousDay')}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
              <th className="pb-2 font-medium">{t('analytics.colDate')}</th>
              <th className="pb-2 font-medium text-right">{t('analytics.visitors')}</th>
              <th className="pb-2 font-medium text-right">{t('analytics.colTrend')}</th>
              <th className="pb-2 font-medium text-right">{t('analytics.pageViews')}</th>
              <th className="pb-2 font-medium text-right">{t('analytics.colTrend')}</th>
              <th className="pb-2 font-medium text-right">{t('analytics.signups')}</th>
              <th className="pb-2 font-medium text-right">{t('analytics.colTrend')}</th>
              <th className="pb-2 font-medium text-right">{t('analytics.colPaid')}</th>
              <th className="pb-2 font-medium text-right">{t('analytics.colActiveUsers')}</th>
              <th className="pb-2 font-medium text-right">{t('analytics.colTrend')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((day, i) => {
              // The previous day in chronological order is the next item in our reversed array
              const prev = i < rows.length - 1 ? rows[i + 1] : undefined
              const prod = productByDate.get(day.date)
              const prevProd = prev ? productByDate.get(prev.date) : undefined
              const isToday = i === 0

              return (
                <tr
                  key={day.date}
                  className={`border-b border-gray-50 hover:bg-gray-50 ${isToday ? 'bg-sky-50/40' : ''}`}
                >
                  <td className="py-2 pr-3 font-medium text-gray-800 whitespace-nowrap">
                    {day.date.slice(5)}
                    {isToday && (
                      <Badge tone="accent" className="ml-1.5">
                        {t('analytics.today')}
                      </Badge>
                    )}
                  </td>
                  <td className="py-2 text-right text-gray-700">{fmt(day.visitors)}</td>
                  <td className="py-2 text-right">
                    <DayTrend current={day.visitors} previous={prev?.visitors} />
                  </td>
                  <td className="py-2 text-right text-gray-700">{fmt(day.page_views)}</td>
                  <td className="py-2 text-right">
                    <DayTrend current={day.page_views} previous={prev?.page_views} />
                  </td>
                  <td className="py-2 text-right text-gray-700">{fmt(day.signups)}</td>
                  <td className="py-2 text-right">
                    <DayTrend current={day.signups} previous={prev?.signups} />
                  </td>
                  <td className="py-2 text-right text-gray-700">{fmt(day.subscriptions)}</td>
                  <td className="py-2 text-right text-gray-700">{fmt(prod?.active_users ?? 0)}</td>
                  <td className="py-2 text-right">
                    <DayTrend
                      current={prod?.active_users ?? 0}
                      previous={prevProd?.active_users}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {rows.length > 7 && (
        <LinkButton className="mt-3" onClick={() => setExpanded(!expanded)}>
          {expanded ? t('analytics.showLess') : t('analytics.showAllDays', { n: rows.length })}
        </LinkButton>
      )}
    </Card>
  )
}

/* ── Visitor map ──────────────────────────────────────────────────────────── */

function VisitorMap({ data }: { data: GeoStat[] }) {
  const points = useMemo(
    () => data.filter((g) => g.lat != null && g.lng != null && g.visitors > 0),
    [data],
  )

  if (points.length === 0) return null

  const maxVisitors = Math.max(...points.map((p) => p.visitors))

  return (
    <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: 360 }}>
      <MapContainer
        center={[30, 10]}
        zoom={2}
        minZoom={2}
        maxZoom={12}
        scrollWheelZoom={true}
        className="bg-gray-50" style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {points.map((g, i) => {
          const r = 6 + (g.visitors / maxVisitors) * 20
          return (
            <CircleMarker
              key={i}
              center={[g.lat!, g.lng!]}
              radius={r}
              pathOptions={{
                fillColor: '#6366f1',
                fillOpacity: 0.55,
                color: '#4f46e5',
                weight: 1,
              }}
            >
              <LTooltip>
                <div className="text-xs">
                  <strong>{[g.city, g.region, g.country].filter(Boolean).join(', ')}</strong>
                  <br />
                  {g.visitors} visitor{g.visitors !== 1 ? 's' : ''}
                  {g.signups > 0 && <> &middot; {g.signups} signup{g.signups !== 1 ? 's' : ''}</>}
                  {g.isp && <><br />{g.isp}</>}
                </div>
              </LTooltip>
            </CircleMarker>
          )
        })}
      </MapContainer>
    </div>
  )
}

/* ── Skeleton loaders ─────────────────────────────────────────────────────── */

function CardSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className={`grid grid-cols-2 md:grid-cols-${count} gap-4 mb-6`}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <Skeleton className="h-3 w-2/3 mb-3" />
          <Skeleton className="h-6 w-1/2" />
        </Card>
      ))}
    </div>
  )
}

function TableSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  )
}

function ChartSkeleton() {
  return (
    <div className="h-56 flex items-center justify-center text-brand-600">
      <Spinner className="h-8 w-8" />
    </div>
  )
}

/* ── Main page ────────────────────────────────────────────────────────────── */

export default function AnalyticsPage() {
  const { t } = useTranslation()
  const [range, setRange] = useState<RangeValue>('30')
  const days = Number(range)

  const { data: summary, isLoading: summaryLoading } = useAnalyticsSummary(days)
  const { data: campaigns, isLoading: campaignsLoading } = useAnalyticsCampaigns(days)
  const { data: geoData, isLoading: geoLoading } = useAnalyticsGeo(days)
  const { data: userUsage = [], isLoading: userUsageLoading } = useAnalyticsUsers(days)
  const acquisitionStats = summary?.acquisition_stats ?? summary?.daily_stats ?? []
  const productStats = summary?.product_stats ?? []

  const pc = summary?.period_comparison
  const bot = summary?.bot_traffic

  return (
    <div className="p-4 sm:p-8 space-y-8">
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <PageHeader
        title={t('analytics.title')}
        description={t('analytics.subtitle')}
        actions={
          <SegmentedControl
            value={range}
            onChange={setRange}
            options={RANGES}
            label={t('analytics.rangeLabel')}
          />
        }
      />

      {/* ── Acquisition overview ────────────────────────────────────────── */}
      <section>
        <SectionHeader
          title={t('analytics.acquisition')}
          description={t('analytics.acquisitionDesc')}
        />
        {summaryLoading ? (
          <CardSkeleton count={5} />
        ) : summary ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <StatCard
                label={t('analytics.visitors')}
                value={fmt(summary.marketing_visitors)}
                trend={pc?.visitors_change}
                accent="bg-sky-500"
                sub={t('analytics.newReturningSub', { newCount: fmt(summary.new_visitors), returningCount: fmt(summary.returning_visitors) })}
              />
              <StatCard
                label={t('analytics.pageViews')}
                value={fmt(summary.marketing_page_views)}
                trend={pc?.page_views_change}
              />
              <StatCard
                label={t('analytics.bounceRate')}
                value={`${fmt(summary.bounce_rate, 1)}%`}
                sub={t('analytics.pagesPerSession', { n: fmt(summary.avg_pages_per_session, 1) })}
              />
              <StatCard
                label={t('analytics.signups')}
                value={fmt(summary.signups)}
                trend={pc?.signups_change}
                accent="bg-emerald-500"
                sub={t('analytics.ofVisitors', { pct: fmt(summary.visit_to_signup_rate, 1) })}
              />
              <StatCard
                label={t('analytics.paidSubscriptions')}
                value={fmt(summary.subscriptions)}
                trend={pc?.subscriptions_change}
                accent="bg-indigo-500"
                sub={t('analytics.ofVisitors', { pct: fmt(summary.visit_to_paid_rate, 1) })}
              />
            </div>

            {/* Conversion funnel */}
            <ConversionFunnel
              visitors={summary.marketing_visitors}
              checkouts={summary.checkout_starts}
              signups={summary.signups}
              subscriptions={summary.subscriptions}
              visitToSignup={summary.visit_to_signup_rate}
              signupToPaid={summary.signup_to_paid_rate}
            />
          </>
        ) : null}

        {/* Acquisition trend chart */}
        <Card>
          <h3 className="text-sm font-medium text-gray-700 mb-4">{t('analytics.visitorsAndSignupsOverTime')}</h3>
          {summaryLoading ? (
            <ChartSkeleton />
          ) : acquisitionStats.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={acquisitionStats} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" className="stroke-gray-200" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#6b7280', className: 'fill-gray-500' }}
                  tickFormatter={(v: string) => v.slice(5)}
                />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280', className: 'fill-gray-500' }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="visitors"
                  stroke="#0ea5e9"
                  strokeWidth={2}
                  dot={false}
                  name={t('analytics.visitors')}
                />
                <Line
                  type="monotone"
                  dataKey="signups"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={false}
                  name={t('analytics.signups')}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState className="h-56 justify-center" title={t('analytics.noPeriodData')} />
          )}
        </Card>
      </section>

      {/* ── Daily breakdown ──────────────────────────────────────────────── */}
      {summary && (acquisitionStats.length > 0 || productStats.length > 0) && (
        <section>
          <SectionHeader
            title={t('analytics.dailyBreakdown')}
            description={t('analytics.dailyBreakdownDesc')}
          />
          <DailyBreakdownTable
            acquisitionStats={acquisitionStats}
            productStats={productStats}
          />
        </section>
      )}

      {/* ── Product usage ───────────────────────────────────────────────── */}
      <section>
        <SectionHeader
          title={t('analytics.productUsage')}
          description={t('analytics.productUsageDesc')}
        />
        {summary ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard label={t('analytics.activeUsers')} value={fmt(summary.product_active_users)} accent="bg-indigo-500" />
              <StatCard label={t('analytics.sessions')} value={fmt(summary.product_sessions)} />
              <StatCard label={t('analytics.appPageViews')} value={fmt(summary.app_page_views)} />
              <StatCard
                label={t('analytics.logins')}
                value={fmt(summary.logins)}
                sub={t('analytics.signupToPaidSub', { pct: fmt(summary.signup_to_paid_rate, 1) })}
              />
            </div>

            <Card>
              <h3 className="text-sm font-medium text-gray-700 mb-4">{t('analytics.activeUsersOverTime')}</h3>
              {summaryLoading ? (
                <ChartSkeleton />
              ) : productStats.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={productStats} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" className="stroke-gray-200" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#6b7280', className: 'fill-gray-500' }}
                      tickFormatter={(v: string) => v.slice(5)}
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#6b7280', className: 'fill-gray-500' }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line
                      type="monotone"
                      dataKey="active_users"
                      stroke="#6366f1"
                      strokeWidth={2}
                      dot={false}
                      name={t('analytics.activeUsers')}
                    />
                    <Line
                      type="monotone"
                      dataKey="app_page_views"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={false}
                      name={t('analytics.appPageViews')}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState className="h-56 justify-center" title={t('analytics.noProductData')} />
              )}
            </Card>

            {/* Per-user in-app usage (legitimate interest; opted-out users
                have no attributed rows and never appear here). */}
            <Card padded={false} className="mt-6 overflow-hidden">
              <div className="px-5 pt-5 pb-3">
                <h3 className="text-sm font-medium text-gray-700">{t('analytics.userUsageTitle')}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{t('analytics.userUsageDesc')}</p>
              </div>
              {userUsageLoading ? (
                <div className="px-5 pb-5"><ChartSkeleton /></div>
              ) : userUsage.length === 0 ? (
                <EmptyState className="py-10" title={t('analytics.noUserUsage')} />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr className="text-left text-xs text-gray-500 uppercase tracking-wide">
                        <th className="pl-5 pr-3 py-2.5 font-medium">{t('analytics.userCol')}</th>
                        <th className="px-3 py-2.5 font-medium text-right">{t('analytics.pageViews')}</th>
                        <th className="px-3 py-2.5 font-medium text-right">{t('analytics.sessions')}</th>
                        <th className="px-3 py-2.5 font-medium hidden md:table-cell">{t('analytics.topPathCol')}</th>
                        <th className="pl-3 pr-5 py-2.5 font-medium hidden sm:table-cell">{t('analytics.lastActiveCol')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {userUsage.map((u) => (
                        <tr key={u.user_id}>
                          <td className="pl-5 pr-3 py-2.5">
                            <div className="font-medium text-gray-900 truncate max-w-[16rem]">{u.email}</div>
                            {u.company_name && <div className="text-xs text-gray-500 truncate max-w-[16rem]">{u.company_name}</div>}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{fmt(u.page_views)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{fmt(u.sessions)}</td>
                          <td className="px-3 py-2.5 hidden md:table-cell">
                            {u.top_path && <code className="text-xs text-gray-600">{u.top_path}</code>}
                          </td>
                          <td className="pl-3 pr-5 py-2.5 hidden sm:table-cell text-xs text-gray-500">
                            {u.last_active ? new Date(u.last_active).toLocaleString() : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        ) : null}
      </section>

      {/* ── Traffic quality ──────────────────────────────────────────────── */}
      <section>
        <SectionHeader
          title={t('analytics.trafficQuality')}
          description={t('analytics.trafficQualityDesc')}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {bot ? (
            <BotTrafficPanel
              botPageViews={bot.bot_page_views}
              humanPageViews={bot.human_page_views}
              botVisitors={bot.bot_visitors}
              humanVisitors={bot.human_visitors}
              botPercentage={bot.bot_percentage}
            />
          ) : summaryLoading ? (
            <Card>
              <TableSkeleton rows={3} />
            </Card>
          ) : null}

          {/* New vs Returning */}
          {summary ? (
            <Card>
              <h3 className="text-sm font-medium text-gray-700 mb-4">{t('analytics.newVsReturning')}</h3>
              <div className="flex gap-6">
                <div className="flex-1 text-center">
                  <p className="text-3xl font-semibold text-sky-600">{fmt(summary.new_visitors)}</p>
                  <p className="text-sm text-gray-500 mt-1">{t('analytics.newLabel')}</p>
                  <p className="text-xs text-gray-400">
                    {summary.marketing_visitors > 0
                      ? `${fmt((summary.new_visitors / summary.marketing_visitors) * 100, 1)}%`
                      : '-'}
                  </p>
                </div>
                <div className="w-px bg-gray-200" />
                <div className="flex-1 text-center">
                  <p className="text-3xl font-semibold text-indigo-600">
                    {fmt(summary.returning_visitors)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{t('analytics.returningLabel')}</p>
                  <p className="text-xs text-gray-400">
                    {summary.marketing_visitors > 0
                      ? `${fmt((summary.returning_visitors / summary.marketing_visitors) * 100, 1)}%`
                      : '-'}
                  </p>
                </div>
              </div>
              {/* Simple visual bar */}
              {summary.marketing_visitors > 0 && (
                <div className="mt-5 flex rounded-full overflow-hidden h-3 bg-gray-100">
                  <div
                    className="bg-sky-500 transition-all"
                    style={{
                      width: `${(summary.new_visitors / summary.marketing_visitors) * 100}%`,
                    }}
                  />
                  <div className="bg-indigo-500 flex-1" />
                </div>
              )}
              <div className="flex justify-between mt-2 text-xs text-gray-400">
                <span>{t('analytics.newVisitorsLabel')}</span>
                <span>{t('analytics.returningVisitorsLabel')}</span>
              </div>
            </Card>
          ) : summaryLoading ? (
            <Card>
              <TableSkeleton rows={3} />
            </Card>
          ) : null}
        </div>
      </section>

      {/* ── Geographic breakdown ─────────────────────────────────────────── */}
      <section>
        <SectionHeader
          title={t('analytics.geography')}
          description={t('analytics.geographyDesc')}
        />
        <Card>
          {geoLoading ? (
            <TableSkeleton rows={4} />
          ) : geoData && geoData.length > 0 ? (
            <>
              <VisitorMap data={geoData} />
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                      <th className="pb-2 font-medium">{t('analytics.colCountry')}</th>
                      <th className="pb-2 font-medium">{t('analytics.colCity')}</th>
                      <th className="pb-2 font-medium">{t('analytics.colISP')}</th>
                      <th className="pb-2 font-medium text-right">{t('analytics.visitors')}</th>
                      <th className="pb-2 font-medium text-right">{t('analytics.colSignups')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {geoData.map((g, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 pr-2 font-medium text-gray-800">{g.country || '-'}</td>
                        <td className="py-2 pr-2 text-gray-600">
                          {g.city || '-'}
                          {g.region && g.city ? (
                            <span className="ml-1 text-gray-400 text-xs">({g.region})</span>
                          ) : g.region ? (
                            <span className="text-gray-500">{g.region}</span>
                          ) : null}
                        </td>
                        <td className="py-2 pr-2 text-gray-600 truncate max-w-[200px]">
                          {g.isp || '-'}
                        </td>
                        <td className="py-2 text-right text-gray-700">{fmt(g.visitors)}</td>
                        <td className="py-2 text-right text-gray-700">{fmt(g.signups)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <EmptyState
              title={
                <span
                  dangerouslySetInnerHTML={{ __html: t('analytics.noGeoData').replace('<code>', '<code class="bg-gray-100 px-1 rounded text-xs">') }}
                />
              }
            />
          )}
        </Card>
      </section>

      {/* ── Campaigns, referrers & pages ────────────────────────────────── */}
      <section>
        <SectionHeader
          title={t('analytics.trafficSources')}
          description={t('analytics.trafficSourcesDesc')}
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Campaign attribution */}
          <Card>
            <h3 className="text-sm font-medium text-gray-700 mb-4">{t('analytics.campaignAttribution')}</h3>
            {campaignsLoading ? (
              <TableSkeleton />
            ) : campaigns && campaigns.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                      <th className="pb-2 font-medium">{t('analytics.colSourceCampaign')}</th>
                      <th className="pb-2 font-medium text-right">{t('analytics.visitors')}</th>
                      <th className="pb-2 font-medium text-right">{t('analytics.colConversion')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((c, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 pr-2">
                          <span className="font-medium text-gray-800">
                            {c.utm_source || '-'}
                          </span>
                          {c.utm_campaign && (
                            <span className="ml-1 text-gray-400 text-xs">/{c.utm_campaign}</span>
                          )}
                        </td>
                        <td className="py-2 text-right text-gray-700">{fmt(c.visitors)}</td>
                        <td className="py-2 text-right">
                          <span
                            className={`text-xs font-medium ${
                              c.conversion_rate >= 5
                                ? 'text-emerald-600'
                                : c.conversion_rate >= 2
                                ? 'text-amber-600'
                                : 'text-gray-500'
                            }`}
                          >
                            {fmt(c.conversion_rate, 1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                title={
                  <span
                    dangerouslySetInnerHTML={{ __html: t('analytics.noCampaignData').replace('<code>', '<code class="bg-gray-100 px-1 rounded text-xs">') }}
                  />
                }
              />
            )}
          </Card>

          {/* Top referrers */}
          <Card>
            <h3 className="text-sm font-medium text-gray-700 mb-4">{t('analytics.topReferrers')}</h3>
            {summaryLoading ? (
              <TableSkeleton />
            ) : summary && summary.top_referrers.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                    <th className="pb-2 font-medium">{t('analytics.colReferrer')}</th>
                    <th className="pb-2 font-medium text-right">{t('analytics.visitors')}</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.top_referrers.map((ref, i) => {
                    const max = summary.top_referrers[0]?.visitors || 1
                    return (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 relative">
                        <td className="py-1.5 pr-2 text-gray-700 truncate max-w-[200px] relative z-10">
                          {ref.referrer}
                        </td>
                        <td className="py-1.5 text-right text-gray-700 relative z-10">
                          {fmt(ref.visitors)}
                        </td>
                        <td
                          className="absolute inset-0 bg-indigo-50 rounded-sm"
                          style={{ width: `${(ref.visitors / max) * 100}%`, opacity: 0.4 }}
                        />
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            ) : (
              <EmptyState title={t('analytics.noReferrerData')} />
            )}
          </Card>

          {/* Top pages */}
          <Card>
            <h3 className="text-sm font-medium text-gray-700 mb-4">{t('analytics.topPages')}</h3>
            {summaryLoading ? (
              <TableSkeleton />
            ) : summary && summary.top_pages.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                    <th className="pb-2 font-medium">{t('analytics.colPage')}</th>
                    <th className="pb-2 font-medium text-right">{t('analytics.colViews')}</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.top_pages.map((pg, i) => {
                    const max = summary.top_pages[0]?.views || 1
                    return (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 relative">
                        <td className="py-1.5 pr-2 text-gray-700 font-mono text-xs truncate max-w-[200px] relative z-10">
                          {pg.path}
                        </td>
                        <td className="py-1.5 text-right text-gray-700 relative z-10">
                          {fmt(pg.views)}
                        </td>
                        <td
                          className="absolute inset-0 bg-sky-50 rounded-sm"
                          style={{ width: `${(pg.views / max) * 100}%`, opacity: 0.4 }}
                        />
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            ) : (
              <EmptyState title={t('analytics.noPageData')} />
            )}
          </Card>
        </div>
      </section>

      {/* ── Privacy note ─────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-500">
        <strong>{t('analytics.privacyLabel')}</strong> {t('analytics.privacyNote')}
      </div>
    </div>
  )
}
